/**
 * SSE subscription for KI-Hofassistent terminal turn-failure signals
 * (#1790 / #1802-FE).
 *
 * When a background assistant turn fails terminally the backend publishes one
 * ``chat:assistant.turn_failed`` (fire-and-forget, never persisted); the BFF
 * relays it on the shared ``/api/events`` stream. This hook is a DUMB
 * subscription — it parses the payload and forwards it to ``onTurnFailed``.
 * All display gating (thread matching, binding the inline "Antwort
 * fehlgeschlagen" + retry row to ``failed_user_message_id``) belongs to the
 * consumer.
 *
 * Cloned from ``useAssistantStepEvents`` (#1776): a per-component EventSource
 * matches the established pattern; ChatPanel unmounts inactive tabs, so a
 * KiAssistantTab-local subscription auto-scopes to the visible tab. Kept a
 * separate hook (not folded into the step subscription) so ``useChatSSE.ts``
 * stays untouched and the two concerns unsubscribe independently.
 */
import { useEffect, useRef } from 'react';

export interface AssistantTurnFailedEvent {
  thread_id: string;
  failed_user_message_id: string;
  reason: string;
}

interface UseAssistantTurnFailedEventsOptions {
  enabled?: boolean;
  onTurnFailed: (evt: AssistantTurnFailedEvent) => void;
}

const isTurnFailedEvent = (payload: unknown): payload is AssistantTurnFailedEvent => {
  if (typeof payload !== 'object' || payload === null) return false;
  const evt = payload as {
    thread_id?: unknown;
    failed_user_message_id?: unknown;
  };
  if (typeof evt.thread_id !== 'string') return false;
  return typeof evt.failed_user_message_id === 'string';
};

export function useAssistantTurnFailedEvents(
  options: UseAssistantTurnFailedEventsOptions,
) {
  const { enabled = true, onTurnFailed } = options;
  // Latest-callback ref (the useChatSSE pattern) so the effect deps stay
  // [enabled] and a re-render never reopens the connection.
  const onTurnFailedRef = useRef(onTurnFailed);
  onTurnFailedRef.current = onTurnFailed;

  useEffect(() => {
    if (!enabled) return;

    const API_URL = import.meta.env.VITE_API_URL || '/api';
    const eventSource = new EventSource(`${API_URL}/events`, {
      withCredentials: true,
    });

    const handler = (e: MessageEvent) => {
      let payload: unknown = null;
      try {
        payload = e.data ? JSON.parse(e.data as string) : null;
      } catch {
        return; // malformed frame — drop silently (telemetry, not data)
      }
      if (!isTurnFailedEvent(payload)) return;
      onTurnFailedRef.current(payload);
    };

    eventSource.addEventListener('chat:assistant.turn_failed', handler);
    return () => {
      eventSource.removeEventListener('chat:assistant.turn_failed', handler);
      eventSource.close();
    };
  }, [enabled]);
}
